import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export type StepProgressStep = {
  title: string;
  description?: string;
};

export type StepProgressProps = {
  /** 1-based active step number. */
  activeStep: number;
  steps: StepProgressStep[];
  ariaLabel: string;
  /** Presence toggles a clickable <button> vs. a static <div> per step. */
  onStepClick?: (step: number) => void;
  /**
   * Display overrides; default to steps[index].title / .description. Members
   * uses these for the active-step-dependent retitling.
   */
  getStepTitle?: (index: number, activeStep: number) => string;
  getStepDescription?: (
    index: number,
    activeStep: number
  ) => string | undefined;
  /**
   * Full Tailwind literal for the lg+ column template, e.g.
   * "lg:grid-cols-[1.2fr_1fr_1fr_1.2fr]". Must be a static string literal at
   * the call site so the Tailwind JIT scanner sees it.
   */
  desktopGridColsClassName: string;
  /**
   * Renders data-testid={`${stepTestIdPrefix}-${stepNumber}`} on clickable
   * steps when provided (Members uses "member-step").
   */
  stepTestIdPrefix?: string;
  /**
   * Optional final non-interactive item rendered after the numbered steps
   * (Events' "Your Event Planner / Coming Soon", EVT-CREATE-001): connector
   * chevron + title/description markup, NO number circle, never clickable —
   * even when onStepClick is provided. Additive-only: consumers that omit it
   * (Members) emit byte-identical DOM.
   */
  trailingItem?: { title: string; description?: string };
};

export const StepProgress = ({
  activeStep,
  steps,
  ariaLabel,
  onStepClick,
  getStepTitle,
  getStepDescription,
  desktopGridColsClassName,
  stepTestIdPrefix,
  trailingItem,
}: StepProgressProps) => (
  <nav
    aria-label={ariaLabel}
    className="sticky top-0 z-20 w-full border-b border-gray-200 bg-white lg:-mx-8 lg:w-auto"
  >
    <ol
      className={`grid w-full grid-cols-1 divide-y divide-gray-100 px-4 ${desktopGridColsClassName} lg:divide-y-0 lg:px-6`}
    >
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === activeStep;
        // With a trailingItem the last numbered step is no longer the last
        // grid cell, so it keeps its trailing padding.
        const isLastStep = stepNumber === steps.length && !trailingItem;
        // The chevron immediately after the ACTIVE step is gold in the mocks;
        // all other chevrons stay dark.
        const connector = index > 0 && (
          <span className="hidden w-5 shrink-0 items-center justify-center lg:flex">
            <ChevronRightIcon
              aria-hidden="true"
              className={
                index === activeStep ? "text-cpaGold" : "text-gray-950"
              }
              sx={{ fontSize: 22 }}
            />
          </span>
        );
        const stepContent = (
          <>
            <span
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold leading-none ${
                isActive
                  ? "bg-cpaGold text-white"
                  : "bg-gray-100 text-gray-950"
              }`}
            >
              {stepNumber}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block whitespace-normal break-words text-sm font-bold leading-5 text-gray-950">
                {getStepTitle ? getStepTitle(index, activeStep) : step.title}
              </span>
              <span className="mt-0.5 block whitespace-normal break-words text-xs leading-5 text-gray-900">
                {getStepDescription
                  ? getStepDescription(index, activeStep)
                  : step.description}
              </span>
            </span>
          </>
        );

        return (
          <li
            key={step.title}
            aria-current={isActive ? "step" : undefined}
            className={`relative flex min-w-0 items-center gap-2 py-4 ${
              index === 0 ? "pl-0" : "pl-0 lg:pl-3"
            } ${isLastStep ? "pr-0" : "pr-3 lg:pr-3"} ${
              onStepClick ? "cursor-pointer" : ""
            }`}
          >
            {onStepClick ? (
              <button
                type="button"
                onClick={() => onStepClick(stepNumber)}
                // Intentionally the RAW step title, not the resolved display
                // title from getStepTitle (matches the Members donor).
                aria-label={`Go to ${step.title}`}
                data-testid={
                  stepTestIdPrefix
                    ? `${stepTestIdPrefix}-${stepNumber}`
                    : undefined
                }
                className="flex w-full min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md bg-transparent text-left focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cpaGold focus-visible:ring-offset-2"
              >
                {connector}
                {stepContent}
              </button>
            ) : (
              <>
                {connector}
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  {stepContent}
                </div>
              </>
            )}
            {isActive && (
              <span className="absolute bottom-[-1px] left-0 h-1 w-full bg-cpaGold" />
            )}
          </li>
        );
      })}
      {trailingItem ? (
        // Non-interactive by construction: no button wrapper, no aria-current
        // (jsx-a11y forbids aria-disabled on an implicit listitem role).
        <li className="relative flex min-w-0 items-center gap-2 py-4 pl-0 pr-0 lg:pl-3">
          {/* The chevron before the trailing item follows the same rule as
              the numbered steps: gold only immediately after the active step
              (activeStep === steps.length), dark otherwise. */}
          <span className="hidden w-5 shrink-0 items-center justify-center lg:flex">
            <ChevronRightIcon
              aria-hidden="true"
              className={
                activeStep === steps.length ? "text-cpaGold" : "text-gray-950"
              }
              sx={{ fontSize: 22 }}
            />
          </span>
          {/* Same title/description markup as the numbered steps but with NO
              number circle and no button wrapper (never clickable). */}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="min-w-0 flex-1">
              <span className="block whitespace-normal break-words text-sm font-bold leading-5 text-gray-950">
                {trailingItem.title}
              </span>
              {trailingItem.description ? (
                <span className="mt-0.5 block whitespace-normal break-words text-xs leading-5 text-gray-900">
                  {trailingItem.description}
                </span>
              ) : null}
            </span>
          </div>
        </li>
      ) : null}
    </ol>
  </nav>
);
